"use client";

import { memo, useCallback, useEffect, useId, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  AnimatePresence,
  useReducedMotion,
  type Variants,
} from "motion/react";
import Container from "@/components/ui/Container";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";
import { ArrowUpRight } from "lucide-react";
import { LAUNCH_BANNERS } from "@/data/launch-banners";

// Motion design tokens — single curve + duration scale across the section.
const EASE = [0.22, 1, 0.36, 1] as const; // strong out-cubic
const DUR = { fast: 0.18, base: 0.28, slow: 0.42 } as const;

/* ── Service data ────────────────────────────────────────────────────────────── */
const SERVICES = [
  {
    num: "01",
    title: "Node as a Service",
    tagline: "Full nodes. Archive. Dedicated. Any chain.",
    description:
      "Spin up production RPC Endpoints and dedicated nodes on 50+ chains in under five minutes. Auto-scaling, global PoPs, and dedicated IP options for teams that need isolation.",
    href: "/solutions/node-as-a-service",
    color: "#6C63FF",
    specs: ["JSON-RPC", "WebSocket", "Archive", "Auto-scale"],
    pattern: "node" as const,
  },
  {
    num: "02",
    title: "Rollup as a Service",
    tagline: "Own your chain. Skip the infra.",
    description:
      "Deploy your rollup on OP Stack or Arbitrum Orbit with sequencer, prover, and DA layer fully managed. You bring the logic. We run the stack.",
    href: "/solutions/rollup-as-a-service",
    color: "#00D4FF",
    specs: ["OP Stack", "Orbit", "Custom DA", "Sequencer"],
    pattern: "rollup" as const,
  },
  {
    num: "03",
    title: "Validator Nodes",
    tagline: "Stake, validate, earn. Without the ops.",
    description:
      "We configure, monitor, and maintain your validator nodes. MEV-optimized, slashing-protected, with auto-reconnect and 24/7 alerting.",
    href: "/solutions/validator-nodes",
    color: "#FA5EFF",
    specs: ["MEV Boost", "Slashing Guard", "24/7 Monitor", "Auto-heal"],
    pattern: "validator" as const,
  },
  {
    num: "04",
    title: "Appchain",
    tagline: "Application-specific chains, expertly built.",
    description:
      "Build your appchain from architecture to launch. Governance, tokenomics, bridge, and explorer delivered as a fully managed service.",
    href: "/solutions/appchain",
    color: "#F59E0B",
    specs: ["Custom VM", "Bridge", "Explorer", "Governance"],
    pattern: "appchain" as const,
  },
  {
    num: "05",
    title: "Dedicated Clusters",
    tagline: "Single-tenant. Engineered to your traffic.",
    description:
      "Isolated blockchain clusters with predictable tail latency, contracted throughput, and multi-region failover. Custom SLAs written into your agreement.",
    href: "/solutions/dedicated-clusters",
    color: "#00E5A0",
    specs: ["Single-tenant", "Multi-region", "Custom SLA", "Observability"],
    pattern: "cluster" as const,
  },
  {
    num: "06",
    title: "Enterprise Blockchain",
    tagline: "Production networks for regulated industries.",
    description:
      "Permissioned and sovereign enterprise blockchain networks for institutions, governments, and large organizations. Architecture, build, and operate under contract.",
    href: "/solutions/enterprise-blockchain",
    color: "#5B8DEF",
    specs: ["Corda", "Hyperledger", "Cosmos SDK", "Substrate"],
    pattern: "enterprise" as const,
  },
];

type ServiceType = (typeof SERVICES)[number];

/* ═══════════════════════════════════════════════════════════════════════════════
   SVG DEFS — gradient faces + filters per card
   ═══════════════════════════════════════════════════════════════════════════════ */
function SvgDefs({ id, color, active }: { id: string; color: string; active: boolean }) {
  return (
    <defs>
      <linearGradient id={`${id}-top`} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="white" stopOpacity={active ? 0.28 : 0.09} />
        <stop offset="55%" stopColor={color} stopOpacity={active ? 0.88 : 0.55} />
        <stop offset="100%" stopColor={color} stopOpacity={active ? 0.65 : 0.38} />
      </linearGradient>
      <linearGradient id={`${id}-right`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={active ? 0.72 : 0.4} />
        <stop offset="100%" stopColor={color} stopOpacity={active ? 0.44 : 0.22} />
      </linearGradient>
      <linearGradient id={`${id}-left`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={active ? 0.28 : 0.13} />
        <stop offset="100%" stopColor={color} stopOpacity={active ? 0.13 : 0.05} />
      </linearGradient>
      <radialGradient id={`${id}-amb`} cx="50%" cy="55%" r="48%">
        <stop offset="0%" stopColor={color} stopOpacity={active ? 0.18 : 0.04} />
        <stop offset="100%" stopColor={color} stopOpacity="0" />
      </radialGradient>
      <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation={active ? "7" : "3"} result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
      <filter id={`${id}-shadow`} x="-20%" y="-20%" width="140%" height="160%">
        <feDropShadow dx="0" dy="5" stdDeviation="7" floodColor={color} floodOpacity={active ? 0.38 : 0.08} />
      </filter>
      <filter id={`${id}-trail`} x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="2" />
      </filter>
    </defs>
  );
}

/* ── Isometric grid ─────────────────────────────────────────────────────────── */
function IsoGrid({ color, opacity }: { color: string; opacity: number }) {
  return (
    <g opacity={opacity}>
      {[0, 1, 2, 3, 4].map((i) => (
        <line key={`h${i}`} x1="10" y1={90 + i * 13} x2="230" y2={90 + i * 13}
          stroke={color} strokeOpacity="0.07" strokeWidth="0.4" strokeDasharray="3 7" />
      ))}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <line key={`v${i}`} x1={18 + i * 28} y1="84" x2={18 + i * 28} y2="145"
          stroke={color} strokeOpacity="0.05" strokeWidth="0.4" strokeDasharray="3 7" />
      ))}
    </g>
  );
}

/* ── Isometric cube with gradient faces ─────────────────────────────────────── */
function IsoCube({
  cx, cy, w, h, gradId, active,
}: {
  cx: number; cy: number; w: number; h: number;
  gradId: string; active: boolean;
}) {
  const hw = w / 2;
  const topPath = `M${cx},${cy - h} L${cx + hw},${cy - h + hw * 0.5} L${cx},${cy - h + w * 0.5} L${cx - hw},${cy - h + hw * 0.5} Z`;
  const leftPath = `M${cx - hw},${cy - h + hw * 0.5} L${cx},${cy - h + w * 0.5} L${cx},${cy} L${cx - hw},${cy - hw * 0.5} Z`;
  const rightPath = `M${cx + hw},${cy - h + hw * 0.5} L${cx},${cy - h + w * 0.5} L${cx},${cy} L${cx + hw},${cy - hw * 0.5} Z`;

  return (
    <g filter={active ? `url(#${gradId}-shadow)` : undefined}>
      <ellipse cx={cx} cy={cy + 5} rx={hw * 0.72} ry={3.2} fill="black" opacity={active ? 0.22 : 0.1} />
      <path d={leftPath} fill={`url(#${gradId}-left)`} />
      <path d={rightPath} fill={`url(#${gradId}-right)`} />
      <path d={topPath} fill={`url(#${gradId}-top)`} />
      <path d={topPath} fill="none" stroke="white" strokeOpacity={active ? 0.2 : 0.06} strokeWidth="0.6" />
      <line
        x1={cx + hw} y1={cy - h + hw * 0.5} x2={cx + hw} y2={cy - hw * 0.5}
        stroke="white" strokeOpacity={active ? 0.09 : 0.03} strokeWidth="0.5"
      />
    </g>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   NODE ILLUSTRATION
   ═══════════════════════════════════════════════════════════════════════════════ */
function NodeIllustration({ color, active, uid }: { color: string; active: boolean; uid: string }) {
  const id = `${uid}-n`;
  return (
    <svg viewBox="0 0 240 150" fill="none" className="absolute inset-0 h-full w-full" data-service-illus="true" aria-hidden>
      <SvgDefs id={id} color={color} active={active} />
      <IsoGrid color={color} opacity={active ? 0.7 : 0.28} />
      <ellipse cx="120" cy="90" rx="72" ry="48" fill={`url(#${id}-amb)`} />

      {/* Three server cubes */}
      <IsoCube cx={120} cy={118} w={56} h={15} gradId={id} active={active} />
      <IsoCube cx={120} cy={102} w={56} h={15} gradId={id} active={active} />
      <IsoCube cx={120} cy={86} w={56} h={15} gradId={id} active={active} />

      {/* Ventilation slots */}
      {[0, 1, 2].map((row) =>
        [0, 1, 2, 3].map((slot) => (
          <rect key={`s${row}-${slot}`}
            x={127 + slot * 5} y={73 + row * 16} width="3" height="1.5" rx="0.6"
            fill="black" opacity={active ? 0.3 : 0.14}
          />
        ))
      )}

      {/* Status LED */}
      <motion.circle cx="120" cy="68" fill={active ? "#00FF88" : color}
        initial={{ r: 3.2, opacity: active ? 1 : 0.28 }}
        animate={active ? { opacity: [1, 0.3, 1], r: [3.2, 4.2, 3.2] } : { opacity: 0.28, r: 3.2 }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      />
      {active && (
        <motion.circle cx="120" cy="68" fill="#00FF88"
          initial={{ r: 6, opacity: 0 }}
          animate={{ opacity: [0, 0.28, 0], r: [6, 11, 6] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        />
      )}

      {/* Orbit ring */}
      <ellipse cx="120" cy="92" rx="46" ry="19"
        fill="none" stroke={color}
        strokeOpacity={active ? 0.22 : 0.06}
        strokeWidth="0.5" strokeDasharray="3 6"
      />

      {/* Orbiting packets */}
      {[0, 1, 2].map((i) => (
        <motion.circle key={i} fill={color}
          initial={{ r: 2.5, opacity: 0.18 }}
          animate={active ? { rotate: [i * 120, i * 120 + 360], r: 3.8, opacity: 0.88 } : { opacity: 0.18, r: 2.5 }}
          transition={active ? {
            rotate: { duration: 5.5, repeat: Infinity, ease: "linear" },
            r: { duration: 0.4 }, opacity: { duration: 0.4 },
          } : { duration: 0.4 }}
          style={{ originX: "120px", originY: "92px", translateX: 166, translateY: 92 }}
        />
      ))}

      {/* Sine throughput wave */}
      {active && (
        <motion.path
          d="M68,134 Q83,126 98,134 Q113,142 128,134 Q143,126 158,134 Q173,142 188,134"
          fill="none" stroke={color} strokeOpacity="0.35" strokeWidth="1.3" strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.9, ease: [0.23, 1, 0.32, 1] }}
        />
      )}
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   ROLLUP ILLUSTRATION
   ═══════════════════════════════════════════════════════════════════════════════ */
function RollupIllustration({ color, active, uid }: { color: string; active: boolean; uid: string }) {
  const id = `${uid}-r`;
  return (
    <svg viewBox="0 0 240 150" fill="none" className="absolute inset-0 h-full w-full" data-service-illus="true" aria-hidden>
      <SvgDefs id={id} color={color} active={active} />
      <defs>
        <linearGradient id={`${id}-iris`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6C63FF" stopOpacity={active ? 0.72 : 0.3} />
          <stop offset="40%" stopColor={color} stopOpacity={active ? 0.92 : 0.45} />
          <stop offset="75%" stopColor="#FA5EFF" stopOpacity={active ? 0.62 : 0.25} />
          <stop offset="100%" stopColor={color} stopOpacity={active ? 0.82 : 0.38} />
        </linearGradient>
        <radialGradient id={`${id}-beam`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity={active ? 0.48 : 0.1} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
      <IsoGrid color={color} opacity={active ? 0.7 : 0.28} />
      <ellipse cx="120" cy="88" rx="80" ry="48" fill={`url(#${id}-amb)`} />

      {/* L1 base slab */}
      <g opacity={active ? 0.75 : 0.38}>
        <path d="M52,120 L120,142 L188,120 L120,98 Z" fill={color} opacity="0.14" />
        <path d="M52,120 L120,142 L188,120 L120,98 Z" fill="none" stroke={color} strokeOpacity="0.55" strokeWidth="0.8" />
        <path d="M52,120 L52,126 L120,148 L120,142 Z" fill={color} opacity="0.07" />
        <path d="M188,120 L188,126 L120,148 L120,142 Z" fill={color} opacity="0.1" />
        <text x="120" y="124" textAnchor="middle" fill={color} fontSize="7.5" fontWeight="700" fontFamily="monospace" opacity="0.7">L1</text>
      </g>

      {/* Light pillar */}
      <rect x="112" y="72" width="16" height="38" fill={`url(#${id}-beam)`} />

      {/* L2 floating plate */}
      <motion.g
        animate={active ? { y: [-7, 0, -7] } : { y: [-2, 0, -2] }}
        transition={{ duration: active ? 3.2 : 6.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <path d="M62,70 L120,88 L178,70 L120,52 Z" fill={color} opacity={active ? 0.1 : 0.04} />
        <path d="M62,64 L120,82 L178,64 L120,46 Z" fill={`url(#${id}-iris)`} />
        <path d="M62,64 L120,82 L178,64 L120,46 Z" fill="none" stroke="white" strokeOpacity={active ? 0.22 : 0.08} strokeWidth="0.7" />
        <path d="M62,64 L62,70 L120,88 L120,82 Z" fill={color} opacity={active ? 0.18 : 0.07} />
        <path d="M178,64 L178,70 L120,88 L120,82 Z" fill={color} opacity={active ? 0.28 : 0.1} />
        <text x="120" y="69" textAnchor="middle" fill="white" fontSize="7.5" fontWeight="700" fontFamily="monospace" opacity={active ? 0.9 : 0.45}>L2</text>
      </motion.g>

      {/* Proof batch stream */}
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.g key={i}>
          <motion.rect
            x={107 + (i % 3) * 11} y={81} width="8" height="6" rx="1.8"
            fill={color} opacity={active ? 0.85 : 0.14}
            animate={active ? { y: [81, 116], opacity: [0.85, 0.85, 0], scaleX: [1, 0.65] } : { opacity: 0.14, y: 81 }}
            transition={active ? { duration: 2.4, repeat: Infinity, ease: "easeIn", delay: i * 0.48 } : {}}
          />
          {active && (
            <motion.text x={111 + (i % 3) * 11} y={86} textAnchor="middle"
              fill="white" fontSize="4" fontWeight="700" fontFamily="monospace" opacity={0.7}
              animate={{ y: [86, 121], opacity: [0.7, 0.7, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeIn", delay: i * 0.48 }}
            >{i + 1}</motion.text>
          )}
        </motion.g>
      ))}

      {/* Sequencer node */}
      <motion.g
        animate={active ? { opacity: [0.45, 1, 0.45] } : { opacity: 0.28 }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <circle cx="194" cy="42" r="13" fill={color} opacity="0.08" stroke={color} strokeOpacity="0.32" strokeWidth="0.8" />
        <text x="194" y="46" textAnchor="middle" fill={color} fontSize="6" fontWeight="700" fontFamily="monospace" opacity="0.7">SEQ</text>
      </motion.g>
      <line x1="178" y1="57" x2="184" y2="48" stroke={color} strokeOpacity={active ? 0.28 : 0.1} strokeWidth="0.7" strokeDasharray="2 3" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   VALIDATOR ILLUSTRATION — 3D shield prism, dual orbit consensus,
   staking beams, force-field ripple, heartbeat monitor, MEV particles
   ═══════════════════════════════════════════════════════════════════════════════ */
function ValidatorIllustration({ color, active, uid }: { color: string; active: boolean; uid: string }) {
  const id = `${uid}-v`;

  /* Outer ring — 5 nodes CW */
  const outerR = 50;
  const outerRy = outerR * 0.38;
  const outerCount = 5;
  const outerColors = ["#6C63FF", "#00D4FF", color, "#F59E0B", "#00FF88"];

  /* Inner ring — 3 nodes CCW */
  const innerR = 26;
  const innerRy = innerR * 0.38;
  const innerCount = 3;
  const innerColors = ["#FF6B6B", "#00D4FF", "#F59E0B"];

  /* Shield geometry — hexagonal prism (3D, not flat) */
  const cx = 120, cy = 72;
  const shieldW = 22, shieldH = 46;
  const hw = shieldW;

  /* Shield face paths (top diamond, left face, right face) */
  const shieldTop = `M${cx},${cy - 8} L${cx + hw},${cy + 2} L${cx},${cy + 12} L${cx - hw},${cy + 2} Z`;
  const shieldLeft = `M${cx - hw},${cy + 2} L${cx},${cy + 12} L${cx},${cy + shieldH - 8} L${cx - hw},${cy + shieldH - 18} Z`;
  const shieldRight = `M${cx + hw},${cy + 2} L${cx},${cy + 12} L${cx},${cy + shieldH - 8} L${cx + hw},${cy + shieldH - 18} Z`;
  /* Shield outline for the pulse ring */
  const shieldOutline = `M${cx},${cy - 12} L${cx + hw + 4},${cy} L${cx + hw + 4},${cy + shieldH - 16} L${cx},${cy + shieldH - 4} L${cx - hw - 4},${cy + shieldH - 16} L${cx - hw - 4},${cy} Z`;

  return (
    <svg viewBox="0 0 240 150" fill="none" className="absolute inset-0 h-full w-full" data-service-illus="true" aria-hidden>
      <SvgDefs id={id} color={color} active={active} />
      <defs>
        {/* Shield face gradients */}
        <linearGradient id={`${id}-sh-top`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="white" stopOpacity={active ? 0.32 : 0.08} />
          <stop offset="50%" stopColor={color} stopOpacity={active ? 0.75 : 0.38} />
          <stop offset="100%" stopColor={color} stopOpacity={active ? 0.55 : 0.28} />
        </linearGradient>
        <linearGradient id={`${id}-sh-left`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={active ? 0.22 : 0.1} />
          <stop offset="100%" stopColor={color} stopOpacity={active ? 0.08 : 0.03} />
        </linearGradient>
        <linearGradient id={`${id}-sh-right`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={active ? 0.5 : 0.22} />
          <stop offset="100%" stopColor={color} stopOpacity={active ? 0.28 : 0.12} />
        </linearGradient>
        {/* Staking beam gradient */}
        <linearGradient id={`${id}-beam`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0" />
          <stop offset="50%" stopColor={color} stopOpacity={active ? 0.55 : 0} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      <IsoGrid color={color} opacity={active ? 0.55 : 0.22} />

      {/* Deep ambient glow */}
      <ellipse cx={cx} cy={cy + 8} rx="78" ry="58" fill={`url(#${id}-amb)`} />

      {/* ── Force-field ripple rings (slashing guard) ────────────────── */}
      {active && [0, 1, 2].map((i) => (
        <motion.path key={`ff${i}`}
          d={shieldOutline}
          fill="none" stroke={color} strokeWidth="0.6"
          initial={{ opacity: 0, scale: 1 }}
          animate={{ opacity: [0.4, 0], scale: [1, 1.35 + i * 0.12] }}
          style={{ originX: `${cx}px`, originY: `${cy + 14}px` }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeOut", delay: i * 0.9 }}
        />
      ))}

      {/* ── Outer orbit ring (CW) ───────────────────────────────────── */}
      <ellipse cx={cx} cy={cy + 8} rx={outerR} ry={outerRy}
        fill="none" stroke={color}
        strokeOpacity={active ? 0.16 : 0.06}
        strokeWidth="0.5" strokeDasharray="4 6"
      />
      <motion.g
        style={{ originX: `${cx}px`, originY: `${cy + 8}px` }}
        animate={{ rotate: 360 }}
        transition={{
          duration: active ? 16 : 40,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        {Array.from({ length: outerCount }).map((_, i) => {
          const a = (i / outerCount) * Math.PI * 2;
          const sx = cx + outerR * Math.cos(a);
          const sy = cy + 8 + outerRy * Math.sin(a);
          const sc = outerColors[i];
          return (
            <g key={`o${i}`}>
              {/* Staking beam — thin line from satellite to shield center */}
              <motion.line x1={sx} y1={sy} x2={cx} y2={cy + 8}
                stroke={sc} strokeWidth="1"
                initial={{ strokeOpacity: 0 }}
                animate={active
                  ? { strokeOpacity: [0.08, 0.35, 0.08] }
                  : { strokeOpacity: 0 }
                }
                transition={active
                  ? { duration: 1.8, repeat: Infinity, delay: i * 0.36 }
                  : { duration: 0.4 }
                }
              />
              {/* Satellite node */}
              <motion.circle cx={sx} cy={sy}
                fill={sc}
                initial={{ r: 4.5, opacity: 0.26 }}
                animate={{ r: active ? 6 : 4.5, opacity: active ? 0.78 : 0.26 }}
                transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
              />
              <motion.circle cx={sx} cy={sy}
                fill="none" stroke="white" strokeWidth="0.5"
                initial={{ r: 4.5, strokeOpacity: 0.06 }}
                animate={{ r: active ? 6 : 4.5, strokeOpacity: active ? 0.18 : 0.06 }}
                transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
              />
              {/* Pulse ring on active */}
              {active && (
                <motion.circle cx={sx} cy={sy} fill="none" stroke={sc} strokeWidth="0.8"
                  initial={{ r: 6, opacity: 0.5 }}
                  animate={{ opacity: [0.5, 0], r: [6, 12] }}
                  transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.36 }}
                />
              )}
            </g>
          );
        })}
      </motion.g>

      {/* ── Inner orbit ring (CCW) ──────────────────────────────────── */}
      <ellipse cx={cx} cy={cy + 8} rx={innerR} ry={innerRy}
        fill="none" stroke={color}
        strokeOpacity={active ? 0.1 : 0.04}
        strokeWidth="0.4" strokeDasharray="2 5"
      />
      <motion.g
        style={{ originX: `${cx}px`, originY: `${cy + 8}px` }}
        animate={{ rotate: -360 }}
        transition={{
          duration: active ? 9 : 28,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        {Array.from({ length: innerCount }).map((_, i) => {
          const a = (i / innerCount) * Math.PI * 2 + Math.PI / 6;
          const sx = cx + innerR * Math.cos(a);
          const sy = cy + 8 + innerRy * Math.sin(a);
          const sc = innerColors[i];
          return (
            <g key={`in${i}`}>
              <circle cx={sx} cy={sy} r={active ? 4 : 3} fill={sc} opacity={active ? 0.7 : 0.22} />
              <circle cx={sx} cy={sy} r={active ? 4 : 3} fill="none" stroke="white" strokeOpacity="0.1" strokeWidth="0.4" />
            </g>
          );
        })}
      </motion.g>

      {/* ── 3D Shield prism ─────────────────────────────────────────── */}
      <motion.g filter={active ? `url(#${id}-glow)` : undefined}>
        {/* Ground shadow */}
        <ellipse cx={cx} cy={cy + shieldH - 4} rx={hw * 0.7} ry={4} fill="black" opacity={active ? 0.25 : 0.1} />
        {/* Left face — darkest */}
        <path d={shieldLeft} fill={`url(#${id}-sh-left)`} />
        {/* Right face — medium */}
        <path d={shieldRight} fill={`url(#${id}-sh-right)`} />
        {/* Top face — brightest */}
        <path d={shieldTop} fill={`url(#${id}-sh-top)`} />
        {/* Edge highlights */}
        <path d={shieldTop} fill="none" stroke="white" strokeOpacity={active ? 0.22 : 0.06} strokeWidth="0.7" />
        <line x1={cx + hw} y1={cy + 2} x2={cx + hw} y2={cy + shieldH - 18}
          stroke="white" strokeOpacity={active ? 0.1 : 0.03} strokeWidth="0.5" />
        {/* Shield border glow */}
        <path
          d={`M${cx},${cy - 8} L${cx + hw},${cy + 2} L${cx + hw},${cy + shieldH - 18} L${cx},${cy + shieldH - 8} L${cx - hw},${cy + shieldH - 18} L${cx - hw},${cy + 2} Z`}
          fill="none" stroke={color}
          strokeOpacity={active ? 0.55 : 0.18} strokeWidth="0.8"
        />

        {/* Inner panel glow — recessed highlight */}
        <path
          d={`M${cx},${cy - 2} L${cx + hw * 0.6},${cy + 5} L${cx + hw * 0.6},${cy + shieldH - 22} L${cx},${cy + shieldH - 14} L${cx - hw * 0.6},${cy + shieldH - 22} L${cx - hw * 0.6},${cy + 5} Z`}
          fill={color} opacity={active ? 0.08 : 0.02}
        />

        {/* Checkmark — animated draw */}
        <motion.path
          d={`M${cx - 9},${cy + 12} L${cx - 2},${cy + 19} L${cx + 12},${cy + 5}`}
          fill="none" stroke={active ? "#00FF88" : color}
          strokeOpacity={active ? 0.95 : 0.25}
          strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: active ? 1 : 0.3 }}
          transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
        />
        {/* Checkmark glow halo */}
        {active && (
          <motion.path
            d={`M${cx - 9},${cy + 12} L${cx - 2},${cy + 19} L${cx + 12},${cy + 5}`}
            fill="none" stroke="#00FF88" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
            filter={`url(#${id}-trail)`}
            animate={{ opacity: [0.15, 0.35, 0.15] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
      </motion.g>

      {/* ── MEV boost particles — tiny diamonds orbiting tight ──────── */}
      {active && [0, 1, 2, 3, 4, 5].map((i) => (
        <motion.g key={`mev${i}`}
          style={{ originX: `${cx}px`, originY: `${cy + 8}px` }}
          animate={{ rotate: [i * 60, i * 60 + 360] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "linear" }}
        >
          <rect
            x={cx + 16} y={cy + 6} width="3.5" height="3.5" rx="0.5"
            fill={color} opacity={0.55}
            transform={`rotate(45,${cx + 17.75},${cy + 7.75})`}
          />
        </motion.g>
      ))}

      {/* ── Heartbeat monitor (24/7 uptime) ──────────────────────────── */}
      <g opacity={active ? 0.65 : 0.15}>
        {/* Baseline */}
        <line x1="32" y1="132" x2="208" y2="132" stroke={color} strokeOpacity="0.15" strokeWidth="0.5" />
        {/* ECG path */}
        <motion.path
          d="M32,132 L62,132 L68,132 L72,122 L76,142 L80,118 L84,138 L88,132 L118,132 L124,132 L128,122 L132,142 L136,118 L140,138 L144,132 L174,132 L180,132 L184,122 L188,142 L192,118 L196,138 L200,132 L208,132"
          fill="none" stroke={active ? "#00FF88" : color}
          strokeWidth={active ? 1.4 : 0.8}
          strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: active ? 1 : 0.4 }}
          transition={active ? {
            pathLength: { duration: 1.8, ease: [0.23, 1, 0.32, 1] },
          } : {
            pathLength: { duration: 0.5, ease: "easeOut" },
          }}
        />
        {/* Scanning dot */}
        {active && (
          <motion.circle r="2.5" fill="#00FF88"
            animate={{ cx: [32, 208], cy: [132, 132] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
          />
        )}
      </g>

      {/* ── Status labels ─────────────────────────────────────────────── */}
      <text x="36" y="128" fill={color} fontSize="5" fontWeight="700"
        fontFamily="monospace" opacity={active ? 0.7 : 0.3}>
        UPTIME
      </text>
      <text x={cx} y="128" textAnchor="middle" fill={color} fontSize="5.5" fontWeight="700"
        fontFamily="monospace" letterSpacing="1.5" opacity={active ? 0.75 : 0.32}>
        CONSENSUS
      </text>
      <text x="200" y="128" textAnchor="end" fill={color} fontSize="5" fontWeight="700"
        fontFamily="monospace" opacity={active ? 0.7 : 0.3}>
        MEV
      </text>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   APPCHAIN ILLUSTRATION
   ═══════════════════════════════════════════════════════════════════════════════ */
function AppchainIllustration({ color, active, uid }: { color: string; active: boolean; uid: string }) {
  const id = `${uid}-a`;
  const moduleColors = ["#6C63FF", "#FA5EFF", color, "#00FF88"];
  const modules = [
    { cx: 50, cy: 92, label: "VM" },
    { cx: 90, cy: 92, label: "GOV" },
    { cx: 132, cy: 92, label: "DA" },
    { cx: 173, cy: 92, label: "EXP" },
  ];

  return (
    <svg viewBox="0 0 240 150" fill="none" className="absolute inset-0 h-full w-full" data-service-illus="true" aria-hidden>
      <SvgDefs id={id} color={color} active={active} />
      {/* Per-module gradients */}
      <defs>
        {modules.map((_, i) => (
          <g key={i}>
            <linearGradient id={`${id}-m${i}t`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="white" stopOpacity={active ? 0.2 : 0.07} />
              <stop offset="100%" stopColor={moduleColors[i]} stopOpacity={active ? 0.88 : 0.45} />
            </linearGradient>
            <linearGradient id={`${id}-m${i}r`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={moduleColors[i]} stopOpacity={active ? 0.68 : 0.35} />
              <stop offset="100%" stopColor={moduleColors[i]} stopOpacity={active ? 0.38 : 0.16} />
            </linearGradient>
            <linearGradient id={`${id}-m${i}l`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={moduleColors[i]} stopOpacity={active ? 0.26 : 0.11} />
              <stop offset="100%" stopColor={moduleColors[i]} stopOpacity={active ? 0.11 : 0.04} />
            </linearGradient>
          </g>
        ))}
      </defs>
      <IsoGrid color={color} opacity={active ? 0.6 : 0.26} />
      <ellipse cx="112" cy="95" rx="95" ry="48" fill={`url(#${id}-amb)`} />

      {/* Connection lines */}
      {modules.slice(0, -1).map((m, i) => (
        <line key={i} x1={m.cx + 15} y1={m.cy} x2={modules[i + 1].cx - 15} y2={modules[i + 1].cy}
          stroke={color} strokeOpacity={active ? 0.35 : 0.1} strokeWidth="1" />
      ))}

      {/* Bridge arc */}
      <motion.path
        d={`M${modules[0].cx},${modules[0].cy - 24} Q112,30 ${modules[3].cx},${modules[3].cy - 24}`}
        fill="none" stroke={color} strokeOpacity={active ? 0.5 : 0.12}
        strokeWidth="1.2" strokeLinecap="round" strokeDasharray="95"
        initial={{ strokeDashoffset: 95 }}
        animate={active ? { strokeDashoffset: [95, 0, -95] } : { strokeDashoffset: 95 }}
        transition={active ? { duration: 3.8, repeat: Infinity, ease: "easeInOut" } : {}}
      />
      <text x="112" y="36" textAnchor="middle" fill={color} fontSize="6" fontWeight="700"
        fontFamily="monospace" letterSpacing="2" opacity={active ? 0.55 : 0.16}>
        BRIDGE
      </text>

      {/* Bridge particles */}
      {active && [0, 1, 2].map((i) => (
        <motion.circle key={i} r="3.5" fill={moduleColors[i]} opacity={0.85}
          animate={{
            offsetDistance: ["0%", "100%"],
            opacity: [0, 0.9, 0.9, 0],
          }}
          style={{
            offsetPath: `path("M${modules[0].cx},${modules[0].cy - 24} Q112,30 ${modules[3].cx},${modules[3].cy - 24}")`,
          }}
          transition={{ duration: 3, repeat: Infinity, delay: i * 1, ease: "easeInOut" }}
        />
      ))}

      {/* Module blocks */}
      {modules.map((m, i) => {
        const hw = 23 / 2;
        const h = 21;
        const tp = `M${m.cx},${m.cy - h} L${m.cx + hw},${m.cy - h + hw * 0.5} L${m.cx},${m.cy - h + 23 * 0.5} L${m.cx - hw},${m.cy - h + hw * 0.5} Z`;
        const lp = `M${m.cx - hw},${m.cy - h + hw * 0.5} L${m.cx},${m.cy - h + 23 * 0.5} L${m.cx},${m.cy} L${m.cx - hw},${m.cy - hw * 0.5} Z`;
        const rp = `M${m.cx + hw},${m.cy - h + hw * 0.5} L${m.cx},${m.cy - h + 23 * 0.5} L${m.cx},${m.cy} L${m.cx + hw},${m.cy - hw * 0.5} Z`;
        return (
          <motion.g key={i}
            animate={active ? { y: [-5, 0, -5] } : { y: [-2, 0, -2] }}
            transition={{ duration: active ? 2.6 : 5.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.38 }}
          >
            <ellipse cx={m.cx} cy={m.cy + 5} rx={hw * 0.72} ry={3} fill="black" opacity={active ? 0.2 : 0.1} />
            <path d={lp} fill={`url(#${id}-m${i}l)`} />
            <path d={rp} fill={`url(#${id}-m${i}r)`} />
            <path d={tp} fill={`url(#${id}-m${i}t)`} />
            <path d={tp} fill="none" stroke="white" strokeOpacity={active ? 0.18 : 0.06} strokeWidth="0.5" />
            <text x={m.cx} y={m.cy + 15} textAnchor="middle"
              fill={moduleColors[i]} fontSize="5.5" fontWeight="700" fontFamily="monospace"
              opacity={active ? 0.65 : 0.26}>
              {m.label}
            </text>
          </motion.g>
        );
      })}

      <text x="112" y="135" textAnchor="middle" fill={color} fontSize="7" fontWeight="600"
        fontFamily="monospace" letterSpacing="2" opacity={active ? 0.48 : 0.16}>
        APPCHAIN
      </text>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   CLUSTER ILLUSTRATION — single-tenant rack stack, containment perimeter
   that solidifies on active, throughput packets entering/exiting the cluster,
   per-rack status LEDs, TENANT label.
   ═══════════════════════════════════════════════════════════════════════════════ */
function ClusterIllustration({ color, active, uid }: { color: string; active: boolean; uid: string }) {
  const id = `${uid}-cl`;
  const cols = 3;
  const rows = 3;
  const cubeW = 22;
  const cubeH = 12;
  const colGap = 30;
  const rowGap = 18;
  const startX = 120 - ((cols - 1) * colGap) / 2;
  const startY = 70;

  return (
    <svg viewBox="0 0 240 150" fill="none" className="absolute inset-0 h-full w-full" data-service-illus="true" aria-hidden>
      <SvgDefs id={id} color={color} active={active} />
      <IsoGrid color={color} opacity={active ? 0.6 : 0.24} />
      <ellipse cx="120" cy="92" rx="92" ry="50" fill={`url(#${id}-amb)`} />

      {/* Containment perimeter — corner brackets that solidify on active */}
      {[
        { x: 36, y: 38, dx: 12, dy: 12 },
        { x: 204, y: 38, dx: -12, dy: 12 },
        { x: 36, y: 126, dx: 12, dy: -12 },
        { x: 204, y: 126, dx: -12, dy: -12 },
      ].map((c, i) => (
        <g key={i}>
          <motion.line x1={c.x} y1={c.y} x2={c.x + c.dx} y2={c.y}
            stroke={color}
            initial={{ strokeOpacity: 0.12, strokeWidth: 0.8 }}
            animate={{ strokeOpacity: active ? 0.7 : 0.12, strokeWidth: active ? 1.4 : 0.8 }}
            transition={{ duration: DUR.base, ease: EASE }}
          />
          <motion.line x1={c.x} y1={c.y} x2={c.x} y2={c.y + c.dy}
            stroke={color}
            initial={{ strokeOpacity: 0.12, strokeWidth: 0.8 }}
            animate={{ strokeOpacity: active ? 0.7 : 0.12, strokeWidth: active ? 1.4 : 0.8 }}
            transition={{ duration: DUR.base, ease: EASE }}
          />
        </g>
      ))}

      {/* TENANT label — top-left bracket */}
      <text x="44" y="34" fill={color} fontSize="5" fontWeight="700"
        fontFamily="monospace" letterSpacing="1.5" opacity={active ? 0.7 : 0.3}>
        TENANT: YOU
      </text>
      <text x="196" y="34" textAnchor="end" fill={color} fontSize="5" fontWeight="700"
        fontFamily="monospace" letterSpacing="1.5" opacity={active ? 0.7 : 0.3}>
        ISOLATED
      </text>

      {/* Throughput inbound (left → cluster) */}
      {active && [0, 1, 2].map((i) => (
        <motion.circle key={`in${i}`} r="2" fill={color}
          initial={{ cx: 20, cy: startY + 6 + i * rowGap, opacity: 0 }}
          animate={{ cx: [20, startX - 14], opacity: [0, 0.95, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut", delay: i * 0.55 }}
        />
      ))}
      {/* Throughput outbound (cluster → right) */}
      {active && [0, 1, 2].map((i) => (
        <motion.circle key={`out${i}`} r="2" fill="#00FF88"
          initial={{ cx: startX + (cols - 1) * colGap + 14, cy: startY + 6 + i * rowGap, opacity: 0 }}
          animate={{ cx: [startX + (cols - 1) * colGap + 14, 220], opacity: [0, 0.95, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeIn", delay: 0.4 + i * 0.55 }}
        />
      ))}

      {/* 3×3 rack stack */}
      {Array.from({ length: rows }).map((_, r) =>
        Array.from({ length: cols }).map((__, c) => {
          const cx = startX + c * colGap;
          const cy = startY + r * rowGap;
          return (
            <motion.g key={`rk-${r}-${c}`}
              initial={{ opacity: 0.5, y: 0 }}
              animate={active ? { opacity: 1, y: [-1.5, 0, -1.5] } : { opacity: 0.55, y: 0 }}
              transition={{ duration: active ? 2.6 : 0.4, repeat: active ? Infinity : 0, ease: "easeInOut", delay: (r + c) * 0.12 }}
            >
              <IsoCube cx={cx} cy={cy} w={cubeW} h={cubeH} gradId={id} active={active} />
              {/* Per-rack status LED */}
              <motion.circle cx={cx + cubeW / 2 - 3} cy={cy - cubeH + 2.5}
                r="1.2" fill={active ? "#00FF88" : color}
                initial={{ opacity: 0.35 }}
                animate={active ? { opacity: [1, 0.3, 1] } : { opacity: 0.35 }}
                transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: r * 0.18 + c * 0.07 }}
              />
            </motion.g>
          );
        })
      )}

      {/* Capacity sparkline — baseline at bottom */}
      <g opacity={active ? 0.6 : 0.15}>
        <line x1="36" y1="138" x2="204" y2="138" stroke={color} strokeOpacity="0.18" strokeWidth="0.5" />
        <motion.path
          d="M36,138 L52,134 L68,136 L84,128 L100,132 L116,124 L132,130 L148,120 L164,126 L180,118 L196,124 L204,120"
          fill="none" stroke={active ? "#00FF88" : color}
          strokeWidth={active ? 1.2 : 0.7}
          strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: active ? 1 : 0.4 }}
          transition={{ duration: 1.6, ease: [0.23, 1, 0.32, 1] }}
        />
      </g>
      <text x="36" y="134" fill={color} fontSize="4.5" fontWeight="700"
        fontFamily="monospace" opacity={active ? 0.65 : 0.25}>
        CAPACITY
      </text>
      <text x="204" y="134" textAnchor="end" fill={color} fontSize="4.5" fontWeight="700"
        fontFamily="monospace" opacity={active ? 0.65 : 0.25}>
        p99 &lt; 50ms
      </text>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   ENTERPRISE ILLUSTRATION — hexagonal consortium ring of 6 IsoCube pillars
   around a central ledger prism, dashed audit perimeter that rotates on
   active, signed-attestation beams from each pillar to the center.
   ═══════════════════════════════════════════════════════════════════════════════ */
function EnterpriseIllustration({ color, active, uid }: { color: string; active: boolean; uid: string }) {
  const id = `${uid}-ent`;
  const cx = 120;
  const cy = 88;
  const ringR = 56;
  const ringRy = 22;
  const pillarColors = ["#5B8DEF", "#6C63FF", "#00D4FF", "#FA5EFF", "#F59E0B", "#00FF88"];

  // 6 pillar positions on hexagonal ring
  const pillars = Array.from({ length: 6 }).map((_, i) => {
    const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
    return {
      x: cx + ringR * Math.cos(a),
      y: cy + ringRy * Math.sin(a),
      color: pillarColors[i],
    };
  });

  // Central ledger geometry — vertical prism
  const lw = 18;
  const lh = 28;
  const ledgerTop = `M${cx},${cy - 14} L${cx + lw},${cy - 4} L${cx},${cy + 6} L${cx - lw},${cy - 4} Z`;
  const ledgerLeft = `M${cx - lw},${cy - 4} L${cx},${cy + 6} L${cx},${cy + lh} L${cx - lw},${cy + lh - 10} Z`;
  const ledgerRight = `M${cx + lw},${cy - 4} L${cx},${cy + 6} L${cx},${cy + lh} L${cx + lw},${cy + lh - 10} Z`;

  return (
    <svg viewBox="0 0 240 150" fill="none" className="absolute inset-0 h-full w-full" data-service-illus="true" aria-hidden>
      <SvgDefs id={id} color={color} active={active} />
      <defs>
        <linearGradient id={`${id}-ld-top`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="white" stopOpacity={active ? 0.3 : 0.08} />
          <stop offset="100%" stopColor={color} stopOpacity={active ? 0.85 : 0.42} />
        </linearGradient>
        <linearGradient id={`${id}-ld-left`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={active ? 0.22 : 0.08} />
          <stop offset="100%" stopColor={color} stopOpacity={active ? 0.08 : 0.03} />
        </linearGradient>
        <linearGradient id={`${id}-ld-right`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={active ? 0.55 : 0.24} />
          <stop offset="100%" stopColor={color} stopOpacity={active ? 0.3 : 0.12} />
        </linearGradient>
      </defs>

      <IsoGrid color={color} opacity={active ? 0.55 : 0.22} />
      <ellipse cx={cx} cy={cy + 6} rx="86" ry="48" fill={`url(#${id}-amb)`} />

      {/* Audit perimeter — dashed concentric rings, rotate on active */}
      <motion.g
        style={{ originX: `${cx}px`, originY: `${cy + 6}px` }}
        animate={active ? { rotate: 360 } : { rotate: 0 }}
        transition={{ duration: 24, repeat: active ? Infinity : 0, ease: "linear" }}
      >
        <ellipse cx={cx} cy={cy + 6} rx={ringR + 10} ry={ringRy + 5}
          fill="none" stroke={color}
          strokeOpacity={active ? 0.22 : 0.07}
          strokeWidth="0.5" strokeDasharray="2 6"
        />
        <ellipse cx={cx} cy={cy + 6} rx={ringR + 18} ry={ringRy + 9}
          fill="none" stroke={color}
          strokeOpacity={active ? 0.14 : 0.04}
          strokeWidth="0.4" strokeDasharray="1 8"
        />
      </motion.g>

      {/* Consortium ring (static) */}
      <ellipse cx={cx} cy={cy + 6} rx={ringR} ry={ringRy}
        fill="none" stroke={color}
        strokeOpacity={active ? 0.2 : 0.07}
        strokeWidth="0.5" strokeDasharray="3 5"
      />

      {/* Attestation beams — pillar → ledger center */}
      {pillars.map((p, i) => (
        <motion.line key={`beam${i}`}
          x1={p.x} y1={p.y - 4} x2={cx} y2={cy + 4}
          stroke={p.color} strokeWidth="0.9"
          initial={{ strokeOpacity: 0 }}
          animate={active ? { strokeOpacity: [0.1, 0.55, 0.1] } : { strokeOpacity: 0.06 }}
          transition={active
            ? { duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: i * 0.32 }
            : { duration: 0.4 }
          }
        />
      ))}

      {/* Consortium pillars — 6 IsoCubes around ring */}
      {pillars.map((p, i) => (
        <motion.g key={`p${i}`}
          initial={{ opacity: 0.6 }}
          animate={active ? { opacity: 1 } : { opacity: 0.62 }}
          transition={{ duration: DUR.base, ease: EASE, delay: i * 0.05 }}
        >
          <IsoCube cx={p.x} cy={p.y} w={16} h={14} gradId={id} active={active} />
          {/* Identity dot above pillar */}
          <motion.circle cx={p.x} cy={p.y - 18} r="1.6"
            fill={p.color}
            initial={{ opacity: 0.4 }}
            animate={active ? { opacity: [1, 0.4, 1] } : { opacity: 0.4 }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: i * 0.22 }}
          />
        </motion.g>
      ))}

      {/* Central ledger prism */}
      <motion.g filter={active ? `url(#${id}-glow)` : undefined}>
        <ellipse cx={cx} cy={cy + lh + 1} rx={lw * 0.7} ry={3} fill="black" opacity={active ? 0.22 : 0.1} />
        <path d={ledgerLeft} fill={`url(#${id}-ld-left)`} />
        <path d={ledgerRight} fill={`url(#${id}-ld-right)`} />
        <path d={ledgerTop} fill={`url(#${id}-ld-top)`} />
        <path d={ledgerTop} fill="none" stroke="white" strokeOpacity={active ? 0.22 : 0.07} strokeWidth="0.7" />
        <path
          d={`M${cx},${cy - 14} L${cx + lw},${cy - 4} L${cx + lw},${cy + lh - 10} L${cx},${cy + lh} L${cx - lw},${cy + lh - 10} L${cx - lw},${cy - 4} Z`}
          fill="none" stroke={color}
          strokeOpacity={active ? 0.6 : 0.18} strokeWidth="0.8"
        />

        {/* Animated signed-checkmark on ledger top face */}
        <motion.path
          d={`M${cx - 7},${cy - 4} L${cx - 1},${cy + 1} L${cx + 8},${cy - 8}`}
          fill="none" stroke={active ? "#00FF88" : color}
          strokeOpacity={active ? 0.95 : 0.3}
          strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: active ? 1 : 0.3 }}
          transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
        />
      </motion.g>

      {/* Status labels */}
      <text x={cx} y="34" textAnchor="middle" fill={color} fontSize="5.5" fontWeight="700"
        fontFamily="monospace" letterSpacing="2" opacity={active ? 0.7 : 0.22}>
        CONSORTIUM
      </text>
      <text x="36" y="138" fill={color} fontSize="5" fontWeight="700"
        fontFamily="monospace" opacity={active ? 0.65 : 0.22}>
        PERMISSIONED
      </text>
      <text x="204" y="138" textAnchor="end" fill={color} fontSize="5" fontWeight="700"
        fontFamily="monospace" opacity={active ? 0.65 : 0.22}>
        AUDITED
      </text>
    </svg>
  );
}

/* ── Illustration router ─────────────────────────────────────────────────────── */
const ServiceIllustration = memo(function ServiceIllustration({ pattern, color, active, uid }: {
  pattern: string; color: string; active: boolean; uid: string;
}) {
  switch (pattern) {
    case "node": return <NodeIllustration color={color} active={active} uid={uid} />;
    case "rollup": return <RollupIllustration color={color} active={active} uid={uid} />;
    case "validator": return <ValidatorIllustration color={color} active={active} uid={uid} />;
    case "appchain": return <AppchainIllustration color={color} active={active} uid={uid} />;
    case "cluster": return <ClusterIllustration color={color} active={active} uid={uid} />;
    case "enterprise": return <EnterpriseIllustration color={color} active={active} uid={uid} />;
    default: return null;
  }
});

/* ═══════════════════════════════════════════════════════════════════════════════
   SPECULAR HIGHLIGHT — glossy surface that tracks the mouse.
   Responsive radius keeps the highlight subtle on mobile (where the card
   nearly fills the viewport) and richer on desktop.
   ═══════════════════════════════════════════════════════════════════════════════ */
function SpecularHighlight({ x, y }: { x: number; y: number }) {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-2xl"
      style={{
        background: `radial-gradient(clamp(160px, 38vw, 360px) circle at ${x}px ${y}px, color-mix(in oklab, var(--ink) 7%, transparent) 0%, transparent 62%)`,
      }}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   HOLOGRAPHIC BORDER — conic gradient ring that sweeps on hover.
   Honors prefers-reduced-motion (renders a static colored ring instead of
   spinning). Bright stop is mixed with white so it pops on dark canvases
   without blowing out on light.
   ═══════════════════════════════════════════════════════════════════════════════ */
function HoloBorder({ color }: { color: string }) {
  const reduced = useReducedMotion();
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setIsTouch(window.matchMedia("(hover: none) and (pointer: coarse)").matches), 0);
    return () => clearTimeout(id);
  }, []);

  const hotSpot = `color-mix(in oklab, ${color} 65%, white 35%)`;
  const ringStyle: React.CSSProperties = {
    background: `conic-gradient(from 0deg, transparent 0deg, ${color}70 28deg, ${hotSpot} 42deg, ${color}55 58deg, transparent 95deg, transparent 360deg)`,
    padding: "1px",
    borderRadius: "inherit",
    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
  };

  return (
    <motion.div
      className="pointer-events-none absolute inset-[-1px] z-0 rounded-2xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: DUR.base, ease: EASE }}
    >
      {reduced || isTouch ? (
        <div className="absolute inset-0 rounded-2xl" style={ringStyle} />
      ) : (
        <motion.div
          className="absolute inset-0 rounded-2xl"
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 4.2, repeat: Infinity, ease: "linear" }}
          style={ringStyle}
        />
      )}
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SERVICE TILE
   ═══════════════════════════════════════════════════════════════════════════════ */

function ServiceTile({ service }: { service: ServiceType }) {
  const reduced = useReducedMotion();
  const uid = useId().replace(/:/g, "s");
  const showLaunchBadge =
    LAUNCH_BANNERS.qubeticsSolver.enabled &&
    LAUNCH_BANNERS.qubeticsSolver.ribbonTarget === service.title;
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [scrollActive, setScrollActive] = useState(false);
  const [numDisplay, setNumDisplay] = useState("00");
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLAnchorElement>(null);
  const rafRef = useRef<number | null>(null);

  // Mobile scroll-activation: activate illustrations when 60% visible on touch devices.
  // Activates earlier than the prior 70% threshold so users on long mobile pages
  // see the entrance instead of static cards already past the fold.
  useGSAP(
    () => {
      if (!cardRef.current) return;
      const card = cardRef.current;
      const mm = gsap.matchMedia();
      const triggers: ScrollTrigger[] = [];
      mm.add("(hover: none) and (pointer: coarse)", () => {
        const t = ScrollTrigger.create({
          trigger: card,
          start: "top 60%",
          end: "bottom 40%",
          onEnter: () => setScrollActive(true),
          onLeave: () => setScrollActive(false),
          onEnterBack: () => setScrollActive(true),
          onLeaveBack: () => setScrollActive(false),
        });
        triggers.push(t);
      });
      return () => {
        triggers.forEach((t) => t.kill());
      };
    },
    { scope: cardRef },
  );

  // Number badge count-up on scroll-enter. No per-card delay — all four
  // tiles count up when their own trigger fires; the parent grid already
  // staggers entrance via cardVariants.
  useGSAP(
    () => {
      if (!cardRef.current) return;
      const card = cardRef.current;
      const mm = gsap.matchMedia();
      const triggers: ScrollTrigger[] = [];
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const target = parseInt(service.num, 10);
        const proxy = { val: 0 };
        const t = ScrollTrigger.create({
          trigger: card,
          start: "top 85%",
          once: true,
          onEnter: () => {
            gsap.to(proxy, {
              val: target,
              duration: 0.6,
              ease: "power2.out",
              onUpdate: () => {
                setNumDisplay(String(Math.round(proxy.val)).padStart(2, "0"));
              },
            });
          },
        });
        triggers.push(t);
      });
      mm.add("(prefers-reduced-motion: reduce)", () => {
        setNumDisplay(service.num);
      });
      return () => {
        triggers.forEach((t) => t.kill());
      };
    },
    { scope: cardRef },
  );

  // Cleanup any pending RAF on unmount.
  useEffect(() => () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
  }, []);

  // Combined active state: hover (desktop) OR scroll-active (mobile)
  const isActive = hovered || scrollActive;
  // Tilt is suppressed during keyboard focus so screen readers / Tab users
  // don't trigger the 3D transform.
  const tiltEnabled = !reduced && !focused;

  // Spring-physics tilt
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const liftRaw = useMotionValue(0);
  const rotateX = useSpring(rawX, { stiffness: 175, damping: 22 });
  const rotateY = useSpring(rawY, { stiffness: 175, damping: 22 });
  const liftSpring = useSpring(liftRaw, { stiffness: 220, damping: 26 });
  const translateZ = useTransform(liftSpring, (v) => `translateZ(${v}px)`);

  const handleMove = useCallback((e: React.MouseEvent) => {
    if (!tiltEnabled || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    rawX.set(-y * 9);
    rawY.set(x * 7);
    // Throttle React state updates to one per frame; tilt itself stays
    // smooth via Framer's motion values which bypass React reconciliation.
    if (rafRef.current != null) return;
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setMouse({ x: px, y: py });
    });
  }, [tiltEnabled, rawX, rawY]);

  const handleEnter = useCallback(() => {
    setHovered(true);
    if (tiltEnabled) liftRaw.set(14);
  }, [liftRaw, tiltEnabled]);

  const handleLeave = useCallback(() => {
    setHovered(false);
    rawX.set(0);
    rawY.set(0);
    liftRaw.set(0);
  }, [rawX, rawY, liftRaw]);

  // Theme-aware shadow strength. Light canvases get a softer tint to avoid
  // the neon-bleed look that hex-with-alpha shadows produce on warm bg.
  const shadowMd = `0 16px 48px color-mix(in oklab, ${service.color} calc(var(--service-shadow-alpha) * 100%), transparent)`;
  const shadowSoft = `0 0 80px color-mix(in oklab, ${service.color} calc(var(--service-shadow-soft-alpha) * 100%), transparent)`;

  return (
    <motion.a
      ref={cardRef}
      href={service.href}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onMouseMove={handleMove}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className="group relative flex h-full flex-col overflow-visible rounded-2xl will-change-transform outline-none active:scale-[0.985] focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--canvas)]"
      style={{
        perspective: 900,
        rotateX: tiltEnabled ? rotateX : 0,
        rotateY: tiltEnabled ? rotateY : 0,
        transform: tiltEnabled ? translateZ : undefined,
      }}
    >
      {/* Holographic border — activated on hover OR scroll (mobile) */}
      <AnimatePresence>{isActive && <HoloBorder color={service.color} />}</AnimatePresence>

      {/* Card surface */}
      <motion.div
        className="relative flex flex-1 flex-col overflow-hidden rounded-2xl border border-border-subtle bg-bg-card"
        animate={isActive ? {
          borderColor: `${service.color}42`,
          boxShadow: `${shadowMd}, ${shadowSoft}`,
        } : {
          borderColor: "color-mix(in oklab, var(--ink) 14%, transparent)",
          boxShadow: "0 0 0 transparent",
        }}
        transition={{ duration: DUR.slow, ease: EASE }}
      >
        {/* Specular gloss — desktop only (needs mouse coords) */}
        {hovered && !reduced && <SpecularHighlight x={mouse.x} y={mouse.y} />}

        {/* ── Illustration — activated on hover OR scroll (mobile) ───── */}
        <motion.div
          className="relative h-52 w-full overflow-hidden border-b border-border-subtle"
          animate={{
            background: isActive
              ? `color-mix(in oklab, ${service.color} 5%, transparent)`
              : "color-mix(in oklab, var(--ink) 1.5%, transparent)",
          }}
          transition={{ duration: DUR.slow, ease: EASE }}
        >
          <ServiceIllustration pattern={service.pattern} color={service.color} active={isActive} uid={uid} />

          {/* Number badge */}
          <motion.span
            className="absolute left-4 top-4 font-mono text-[12px] font-bold tracking-[0.16em]"
            animate={{ color: isActive ? service.color : "var(--ink-2)" }}
            transition={{ duration: DUR.base, ease: EASE }}
          >
            {numDisplay}
          </motion.span>

          {/* Arrow */}
          <motion.span
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border backdrop-blur-[2px]"
            animate={{
              borderColor: isActive ? `${service.color}60` : "color-mix(in oklab, var(--ink) 35%, transparent)",
              backgroundColor: isActive
                ? `color-mix(in oklab, ${service.color} 14%, transparent)`
                : "color-mix(in oklab, var(--ink) 5%, transparent)",
              color: isActive ? service.color : "var(--ink-2)",
              scale: hovered ? 1.05 : 1,
              x: hovered ? 2 : 0,
              y: hovered ? -2 : 0,
            }}
            transition={{ duration: DUR.fast, ease: EASE }}
          >
            <ArrowUpRight size={15} aria-hidden />
          </motion.span>

          {/* Launch flag — cut-corner ribbon flush to the card's left edge,
              matches the "new/notable" flag language used elsewhere on site */}
          {showLaunchBadge && (
            <div className="pointer-events-none absolute left-0 top-12 z-20 flex items-center gap-1.5 bg-[#0b0b10] py-1 pl-3 pr-4 [clip-path:polygon(0_0,100%_0,calc(100%-8px)_100%,0_100%)]">
              <span
                aria-hidden
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-purple-light"
                style={{ animation: "pulse 2s ease-in-out infinite" }}
              />
              <span className="font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-white">
                {LAUNCH_BANNERS.qubeticsSolver.ribbonLabel}
              </span>
            </div>
          )}

          {/* Bottom fade */}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-14"
            style={{ background: "linear-gradient(to top, var(--color-bg-card), transparent)" }}
          />
        </motion.div>

        {/* ── Content ──────────────────────────────────────────────────── */}
        <div className="relative z-10 flex flex-1 flex-col p-6 lg:p-7">
          <motion.p
            className="mb-2 text-xs font-semibold tracking-wide"
            animate={{ color: isActive ? service.color : "var(--ink-2)" }}
            transition={{ duration: DUR.base, ease: EASE }}
          >
            {service.tagline}
          </motion.p>

          <h3 className="mb-3 text-xl font-semibold text-ink">{service.title}</h3>
          <p className="mb-6 flex-1 text-base leading-relaxed text-ink-2">{service.description}</p>

          {/* Spec list — dot-separated mono metadata */}
          <p
            className="font-mono text-[11px] tracking-wide"
            style={{
              color: isActive
                ? `color-mix(in oklab, ${service.color} 70%, var(--ink-2))`
                : "color-mix(in oklab, var(--ink) 38%, transparent)",
              transition: `color ${DUR.base}s`,
            }}
          >
            {service.specs.join(" · ")}
          </p>
        </div>
      </motion.div>
    </motion.a>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SECTION
   ═══════════════════════════════════════════════════════════════════════════════ */
const cardVariants: Variants = {
  hidden: { opacity: 0, y: 24, scale: 0.98 },
  visible: (i: number) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.6, delay: i * 0.08, ease: EASE },
  }),
};

export default function ServicesGrid() {
  const sectionRef = useRef<HTMLElement>(null);
  useScrollReveal(sectionRef, 0.08);

  // Header + cards rely on motion's whileInView and CSS .reveal; no GSAP needed.
  // Avoids stacked hide-states that left heading invisible when triggers
  // were delayed by Lenis sync.

  return (
    <section ref={sectionRef} className="section-pad">
      <Container>
        {/* Header — theme-aware, four-offerings explainer */}
        <div className="sg-header mb-14 grid items-end gap-6 md:grid-cols-[1fr_auto]">
          <div>
            <h2 className="font-accent text-[clamp(32px,4.5vw,52px)] font-normal leading-[1.06] tracking-[-0.025em] text-ink">
              Everything you need to <em className="italic text-accent">run blockchain infrastructure</em>.
            </h2>
          </div>
          <p className="max-w-xs text-base leading-[1.55] text-ink-2 md:text-right">
            From a single RPC Endpoint to enterprise blockchain networks. We handle the ops.
          </p>
        </div>

        {/* Grid */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6" style={{ perspective: 1400 }}>
          {SERVICES.map((service, i) => (
            <motion.div
              key={service.num}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "20px" }}
              className="h-full"
            >
              <ServiceTile service={service} />
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
